import { ICellRendererAngularComp } from 'ag-grid-angular';
import { IRowNode } from 'ag-grid-community';

import { Component, HostListener } from '@angular/core';
import { ProgressBarStatus } from '@core/enums/progress-bar-status.enum';
import { PeriodRendererParams } from '@core/typings/period-renderer.params';
import { NodeUtils } from '@core/utils/node.utils';
import { BaseComponent } from '@portal-shared/app/components/base-components/base.component';
import { ContextMenuService } from '@portal-shared/app/components/context-menu/context-menu.service';
import { ContextMenuItem } from '@portal-shared/app/components/context-menu/models/context-menu-item';
import { Period } from '@portal-shared/app/entities/period.model';
import { FormatNumberPipe } from '@portal-shared/app/pipes/format-number.pipe';
import { ModalService } from '@portal-shared/app/services/modal.service';
import { GuidString } from '@portal-shared/app/typings/guid-string.type';
import { AssignmentPeriodService } from '@services/assignment-period.service';
import { ProjectPeriodUsageDto } from '@services/dtos/rows/project-period-usage.dto';
import { ProjectService } from '@services/project.service';
import { CellDropZoneComponent } from '@shared/components/cell-drop-zone/cell-drop-zone.component';
import { DepartmentGroupRow } from '@shared/components/grids/rows/groups/department-group.row';
import { PlanningGroupRow } from '@shared/components/grids/rows/groups/planning-group.row';
import { ProjectGroupRow } from '@shared/components/grids/rows/groups/project-group.row';
import { ProgressBarComponent } from '@shared/components/progress-bar/progress-bar.component';
import { ShiftModalComponent } from '@shared/components/shift-modal/shift-modal.component';
import { PlanningProgressHelper } from '@shared/helpers/planning-progress.helper';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'al-period-planning-progress-cell-renderer',
    templateUrl: './period-planning-progress-cell-renderer.component.html',
    styleUrls: ['./period-planning-progress-cell-renderer.component.scss'],
    imports: [ProgressBarComponent, CellDropZoneComponent, FormatNumberPipe]
})
export class PeriodPlanningProgressCellRendererComponent extends BaseComponent implements ICellRendererAngularComp {
  private disableCellEdit$ = new BehaviorSubject(true);

  public assignedHours = 0;
  public unplannedHours = 0;
  public status = ProgressBarStatus.Neutral;

  public isLoading = true;
  public node?: IRowNode<PlanningGroupRow>;
  public period?: Period;

  public maxValue = 1;
  public showUnplanned = false;

  private contextMenuItems: ContextMenuItem[] = [
    {
      titleKey: 'shared.assignmentBoard.contextMenu.insert',
      subTitleKey: 'shared.assignmentBoard.contextMenu.shiftCellsRight',
      disabled$: this.disableCellEdit$,

      click: () => Promise.resolve(this.rescheduleProjectWithModal())
    },
    {
      titleKey: 'shared.assignmentBoard.contextMenu.delete',
      subTitleKey: 'shared.assignmentBoard.contextMenu.shiftCellsLeft',
      disabled$: this.disableCellEdit$,

      separator: 'below',
      click: async () => await this.rescheduleProjectByRowType(-1)
    }
  ];

  public get isDroppable() {
    return NodeUtils.isCellDroppable(this.node);
  }

  public constructor(
    private projectService: ProjectService,
    private contextMenuService: ContextMenuService,
    private assignmentPeriodService: AssignmentPeriodService,
    private modalService: ModalService
  ) {
    super();
  }

  public agInit(params: PeriodRendererParams<PlanningGroupRow, ProjectPeriodUsageDto>) {
    this.update(params);
  }

  public refresh(params: PeriodRendererParams<PlanningGroupRow, ProjectPeriodUsageDto>): boolean {
    this.update(params);
    return true;
  }

  private update(params: PeriodRendererParams<PlanningGroupRow, ProjectPeriodUsageDto>) {
    if (!params.value) {
      return;
    }

    this.isLoading = false;

    const planningSettings = params.planningBoardSettings;
    const period = params.period;
    const { assignedHours = 0, unassignedHours = 0, requestedHours = 0 } = params.value;

    this.node = params.node;
    this.period = params.period;
    this.assignedHours = assignedHours;
    this.unplannedHours = unassignedHours + requestedHours;
    this.maxValue = this.assignedHours + this.unplannedHours;

    const percentage = PlanningProgressHelper.computeProgressPercentage(
      planningSettings,
      this.assignedHours,
      this.maxValue
    );

    this.showUnplanned = percentage < 100;
    this.status = PlanningProgressHelper.computeStatus(planningSettings, percentage, this.unplannedHours > 0);
    //
    this.disableCellEdit$.next(period.hasActualHours ?? true);
  }

  @HostListener('contextmenu', ['$event'])
  public onContextMenu(event: PointerEvent) {
    this.contextMenuService.onContextMenu(event, this.contextMenuItems);
  }

  public expand(expand = true) {
    if (!this.node?.group) return;

    this.node?.setExpanded(expand);
  }
  public toggleExpand() {
    this.expand(!this.node?.expanded);
  }

  private rescheduleProjectWithModal() {
    const period = this.period;
    const modal = this.modalService.openModal(ShiftModalComponent, modal => {
      if (!period) return;

      modal.sourcePeriod = period;
    });

    this.subscribe(
      modal.observePeriodSelection().subscribe(async selectedPeriod => {
        this.modalService.closeModal();

        if (!period) return;

        const shiftPeriodCount = selectedPeriod.id - period.id;
        await this.rescheduleProjectByRowType(shiftPeriodCount);
      })
    );
  }

  private async rescheduleProjectByRowType(shiftPeriodCount: number) {
    if (!this.node) return;

    let projectId: GuidString;
    let departmentId: GuidString | undefined;

    if (this.node.group) {
      const row = this.node.data;

      if (row instanceof ProjectGroupRow) {
        projectId = row.projectId;
      }

      if (row instanceof DepartmentGroupRow) {
        projectId = row.projectId;
        departmentId = row.departmentId;
      }
    } else {
      return;
    }

    if (!this.period) return;

    if (projectId!) {
      await this.projectService.rescheduleProject(projectId, departmentId, this.period.id, shiftPeriodCount);
    }
  }
}
